import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { lazy, Suspense } from "react";
import { Loader2 } from "lucide-react";
import RootLanding from "./pages/RootLanding";
import ActivationPage from "./pages/Index";
import NotFound from "./pages/NotFound";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import ResetPassword from "./pages/ResetPassword";
import AppWithEditorKeepAlive from "./components/routing/AppWithEditorKeepAlive";
import EditorPlatformSwitcher from "./components/routing/EditorPlatformSwitcher";
import { ChunkErrorBoundary } from "./components/ChunkErrorBoundary";
import { AuthProvider } from "./providers/AuthProvider";
import { FeatureFlagsProvider } from "./providers/FeatureFlagsProvider";
import { ProtectedRoute } from "./components/routing/ProtectedRoute";
import AdminGate from "./components/AdminGate";
import MagicLinkExpired from "./pages/MagicLinkExpired";
import { AuthErrorHandler } from "./components/AuthErrorHandler";
import { UserbackRouteSuppressor } from "./components/UserbackWidget";

// Lazy load with retry for chunk loading failures
function lazyWithRetry<T extends React.ComponentType<unknown>>(
  importFn: () => Promise<{ default: T }>
) {
  return lazy(() =>
    importFn().catch((error) => {
      // If chunk fails to load, try reloading once
      if (
        error.message.includes('Failed to fetch dynamically imported module') ||
        error.message.includes('Loading chunk')
      ) {
        const lastReload = sessionStorage.getItem('chunk_error_reload');
        const now = Date.now();
        if (!lastReload || now - parseInt(lastReload) > 10000) {
          sessionStorage.setItem('chunk_error_reload', now.toString());
          window.location.reload();
        }
      }
      throw error;
    })
  );
}

const Docs = lazyWithRetry(() => import("./pages/Docs"));
const DocView = lazyWithRetry(() => import("./pages/DocView"));
const DocLinkView = lazyWithRetry(() => import("./pages/DocLinkView"));
const Reporting = lazyWithRetry(() => import("./pages/Reporting"));
const ContractSign = lazyWithRetry(() => import("./pages/ContractSign"));
const SnapDoc = lazyWithRetry(() => import("./pages/SnapDoc"));
const Dashboard = lazyWithRetry(() => import("./pages/Dashboard"));
const AdminDashboard = lazyWithRetry(() => import("./pages/AdminDashboard"));
const LegacyEditor = lazyWithRetry(() => import("./pages/LegacyEditor"));
const SnapLinksTest = lazyWithRetry(() => import("./pages/SnapLinksTest"));
const Roadmap = lazyWithRetry(() => import("./pages/Roadmap"));
const ConfirmActivation = lazyWithRetry(() => import("./pages/ConfirmActivation"));
const ConfirmStackActivation = lazyWithRetry(() => import("./pages/ConfirmStackActivation"));
const RetrieveCodes = lazyWithRetry(() => import("./pages/RetrieveCodes"));
const ConfirmTransfer = lazyWithRetry(() => import("./pages/ConfirmTransfer"));
const EmbrFyrAuthorize = lazyWithRetry(() => import("./pages/EmbrFyrAuthorize"));
const UIAudit = lazyWithRetry(() => import("./pages/UIAudit"));
const AnalysisPackPage = lazyWithRetry(() => import("./pages/AnalysisPackPage"));
const LaunchCredit = lazyWithRetry(() => import("./pages/LaunchCredit"));
const LaunchReferral = lazyWithRetry(() => import("./pages/LaunchReferral"));
const LaunchDemo = lazyWithRetry(() => import("./pages/LaunchDemo"));
const LaunchDemoResult = lazyWithRetry(() => import("./pages/LaunchDemoResult"));
const MobileDocumentHarness = lazyWithRetry(() => import("./pages/qa/MobileDocumentHarness"));
const AspectLoopHarness = lazyWithRetry(() => import("./pages/qa/AspectLoopHarness"));
const AspectTweakPanelHarness = lazyWithRetry(() => import("./pages/qa/AspectTweakPanelHarness"));

// Lite mobile editor (separate slim bundle; operates on the same template JSON)
const MobileTemplatesPage = lazyWithRetry(() => import("./mobile/MobileTemplatesPage"));
const MobileEditorPage = lazyWithRetry(() => import("./mobile/MobileEditorPage"));

const queryClient = new QueryClient();
const enableQaRoutes = import.meta.env.DEV || import.meta.env.VITE_ENABLE_QA_ROUTES === "true";

const RouteLoadingFallback = () => (
  <div className="min-h-screen bg-sl-bg flex items-center justify-center">
    <Loader2 className="h-8 w-8 animate-spin text-sl-accent" />
  </div>
);

const LegacyGalleryRedirect = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const tab = (params.get("tab") || "mine").toLowerCase();

  const view =
    tab === "snaplayer"
      ? "snaplayer-templates"
      : tab === "community"
        ? "community-templates"
        : "my-templates";

  return <Navigate to={`/dashboard?view=${view}`} replace />;
};

const LegacySupportRedirect = () => <Navigate to="/dashboard" replace />;

const LegacySnapReportRedirect = () => {
  const { reportId } = useParams<{ reportId?: string }>();
  const target = reportId
    ? `/r/${encodeURIComponent(reportId)}`
    : "/reporting";
  return <Navigate to={target} replace />;
};

const lazyRoute = (element: React.ReactNode) => (
  <Suspense fallback={<RouteLoadingFallback />}>{element}</Suspense>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <FeatureFlagsProvider>
        <AuthProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <AuthErrorHandler>
              <Routes>
                <Route path="/" element={<RootLanding />} />
                <Route path="/activation" element={<ActivationPage />} />
                <Route path="/login" element={<Login />} />
                <Route path="/signup" element={<Signup />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/magic-link-expired" element={<MagicLinkExpired />} />

                <Route
                  path="/integrations/embrfyr/authorize"
                  element={
                    <ProtectedRoute>
                      {lazyRoute(<EmbrFyrAuthorize />)}
                    </ProtectedRoute>
                  }
                />

                {/* Dashboard + editor: editor stays mounted when switching to dashboard */}
                <Route
                  element={
                    <ProtectedRoute>
                      <AppWithEditorKeepAlive />
                    </ProtectedRoute>
                  }
                >
                  <Route
                    path="/dashboard"
                    element={
                      <ChunkErrorBoundary>
                        {lazyRoute(<Dashboard />)}
                      </ChunkErrorBoundary>
                    }
                  />
                  <Route path="/editor" element={null} />
                  <Route path="/editor/:templateId" element={null} />
                </Route>

                {/* Lite mobile editor */}
                <Route
                  path="/m"
                  element={
                    <ProtectedRoute>
                      <ChunkErrorBoundary>{lazyRoute(<MobileTemplatesPage />)}</ChunkErrorBoundary>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/m/new"
                  element={
                    <ProtectedRoute>
                      <ChunkErrorBoundary>{lazyRoute(<MobileEditorPage />)}</ChunkErrorBoundary>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/m/:templateId"
                  element={
                    <ProtectedRoute>
                      <ChunkErrorBoundary>{lazyRoute(<MobileEditorPage />)}</ChunkErrorBoundary>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/account"
                  element={
                    <ProtectedRoute>
                      <Navigate to="/dashboard" state={{ view: "profile" }} replace />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/account/licenses"
                  element={
                    <ProtectedRoute>
                      <Navigate to="/dashboard" state={{ view: "licenses" }} replace />
                    </ProtectedRoute>
                  }
                />
                <Route path="/gallery" element={<LegacyGalleryRedirect />} />
                <Route path="/support" element={<LegacySupportRedirect />} />
                <Route
                  path="/launch-credit"
                  element={
                    <ProtectedRoute>
                      {lazyRoute(<LaunchCredit />)}
                    </ProtectedRoute>
                  }
                />
                <Route path="/launch/:shareCode" element={lazyRoute(<LaunchReferral />)} />
                <Route path="/try/:demoSlug" element={lazyRoute(<LaunchDemo />)} />
                <Route path="/try/:demoSlug/result/:sessionCode" element={lazyRoute(<LaunchDemoResult />)} />
                <Route path="/roadmap" element={lazyRoute(<Roadmap />)} />
                <Route path="/analysis/:packId" element={lazyRoute(<AnalysisPackPage />)} />
                {enableQaRoutes && (
                  <>
                    <Route
                      path="/editor-legacy"
                      element={
                        <ProtectedRoute>
                          {lazyRoute(<LegacyEditor />)}
                        </ProtectedRoute>
                      }
                    />
                    <Route path="/snaplinks-test" element={lazyRoute(<SnapLinksTest />)} />
                    <Route path="/ui-audit" element={lazyRoute(<UIAudit />)} />
                    <Route path="/qa/one-editor/mobile-document-harness" element={lazyRoute(<MobileDocumentHarness />)} />
                    <Route path="/qa/ai/aspect-loop-harness" element={lazyRoute(<AspectLoopHarness />)} />
                    <Route path="/qa/ai/aspect-tweak-panel" element={lazyRoute(<AspectTweakPanelHarness />)} />
                  </>
                )}
                <Route
                  path="/snapdoc"
                  element={
                    <ProtectedRoute>
                      {lazyRoute(<SnapDoc />)}
                    </ProtectedRoute>
                  }
                />
                <Route path="/docs" element={lazyRoute(<Docs />)} />
                <Route path="/doc/:slug" element={lazyRoute(<DocView />)} />
                <Route path="/d/:slug" element={lazyRoute(<DocLinkView />)} />
                <Route
                  path="/docs/document/:definitionId"
                  element={
                    <ProtectedRoute>
                      <Navigate to="/snapdoc" replace />
                    </ProtectedRoute>
                  }
                />
                <Route path="/docs/sign/:signingToken" element={lazyRoute(<ContractSign />)} />
                <Route path="/docs/review/:signingToken" element={lazyRoute(<ContractSign />)} />
                <Route path="/sign/:signingToken" element={lazyRoute(<ContractSign />)} />
                <Route path="/docs/:slug" element={lazyRoute(<Docs />)} />
                <Route path="/reporting" element={lazyRoute(<Reporting />)} />
                <Route path="/r/:slug" element={lazyRoute(<Reporting />)} />
                <Route path="/reporting/reports/:reportIdentity" element={lazyRoute(<Reporting />)} />
                <Route path="/reporting/client/:definitionId" element={lazyRoute(<Reporting />)} />
                <Route path="/reporting/sign/:signingToken" element={lazyRoute(<ContractSign />)} />

                {/* Admin Routes */}
                <Route
                  path="/admin"
                  element={
                    <ProtectedRoute>
                      <AdminGate>
                        {lazyRoute(<AdminDashboard />)}
                      </AdminGate>
                    </ProtectedRoute>
                  }
                />

                {/* Code Activation Routes */}
                <Route path="/activate" element={<ActivationPage />} />
                <Route path="/confirm-activation" element={lazyRoute(<ConfirmActivation />)} />
                <Route path="/confirm-stack-activation" element={lazyRoute(<ConfirmStackActivation />)} />
                <Route path="/confirm-transfer" element={lazyRoute(<ConfirmTransfer />)} />
                <Route path="/retrieve-codes" element={lazyRoute(<RetrieveCodes />)} />

                {/* User Pages */}
                <Route
                  path="/profile"
                  element={
                    <ProtectedRoute>
                      <Navigate to="/dashboard" state={{ view: "profile" }} replace />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings"
                  element={
                    <ProtectedRoute>
                      <Navigate to="/dashboard" state={{ view: "settings" }} replace />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/plan"
                  element={
                    <ProtectedRoute>
                      <Navigate to="/dashboard" state={{ view: "plan-usage" }} replace />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/licenses"
                  element={
                    <ProtectedRoute>
                      <Navigate to="/dashboard" state={{ view: "licenses" }} replace />
                    </ProtectedRoute>
                  }
                />
                {/* Legacy SnapReport viewer URLs now route to the maintained client-report page. */}
                <Route path="/snap/report/:reportId" element={<LegacySnapReportRedirect />} />
                <Route path="/report/:reportId" element={<LegacySnapReportRedirect />} />
                {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                <Route path="*" element={<NotFound />} />
              </Routes>
              <UserbackRouteSuppressor />
              <EditorPlatformSwitcher />
            </AuthErrorHandler>
          </BrowserRouter>
        </AuthProvider>
      </FeatureFlagsProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
